import './PracticeAlerts.css';
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Col, Row, Form, FormGroup, Label, Input, Button, Collapse, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../context';

const PracticeAlerts = ({ practiceId }) => {
    const dispatch = useDispatch();
    const latestAlert = useSelector((state) => state.practiceAlerts.latest);

    // state variables
    const [isCollapsed, setIsCollapsed] = useState(() => !latestAlert?.message);
    const [alertText, setAlertText] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [originalText, setOriginalText] = useState('');
    const [lastUpdatedBy, setLastUpdatedBy] = useState('');
    const [lastUpdatedDate, setLastUpdatedDate] = useState(null);
    const [formattedDate, setformattedDate] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);

    const toggleCollapse = useCallback(() => {
        setIsCollapsed((prev) => !prev);
    }, []);

    // fetch the latest practice alert when the component mounts
    useEffect(() => {
        dispatch(actions.practiceAlerts.fetchLatestPracticeAlert(practiceId));
    }, [dispatch, practiceId]);

    // update the alert text when the latest alert changes
    useEffect(() => {
        if (latestAlert && latestAlert.practiceId === practiceId) {
            setAlertText(latestAlert.message || '');
            setOriginalText(latestAlert.message || '');
            setLastUpdatedBy(`${latestAlert.modifiedBy?.firstName} ${latestAlert.modifiedBy?.lastName}` || 'Unknown');
            setLastUpdatedDate(latestAlert.modifiedDate || null);
            setIsCollapsed(!latestAlert.message);
        } else {
            // reset
            setAlertText('');
            setOriginalText('');
            setLastUpdatedBy('');
            setLastUpdatedDate(null);
            setIsCollapsed(true);
        }
    }, [latestAlert, practiceId]);
    
    const handleSaveClick = useCallback(async () => {
        if (alertText.trim() === '') return;

        try {
            await dispatch(actions.practiceAlerts.savePracticeAlert(practiceId, alertText));
            await dispatch(actions.practiceAlerts.fetchLatestPracticeAlert(practiceId));
            
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to save practice alert:', error);
        }
    }, [alertText, dispatch, practiceId]);

    const handleDeleteClick = useCallback(() => {
        setShowConfirm(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        try {
            if (latestAlert && latestAlert.id) {
                await dispatch(actions.practiceAlerts.deletePracticeAlert(practiceId, latestAlert.id));
                setAlertText('');
                setOriginalText('');
                await dispatch(actions.practiceAlerts.fetchLatestPracticeAlert(practiceId));
            }
        } catch (error) {
            console.error("Failed to delete practice alert:", error);
        }
        setShowConfirm(false);
    }, [dispatch, practiceId, latestAlert])

    const handleCancelClick = () => {
        setAlertText(originalText);
        setIsEditing(false);
    };

    // format the last updated date
    useEffect(() => {
        if (!lastUpdatedDate) {
            setformattedDate('');
        } else {
            try {
                const utcDate = new Date(`${lastUpdatedDate}Z`);

                if (isNaN(utcDate.getTime())) {
                    setformattedDate('Invalid date');
                } else {
                    // convert to EST/EDT time
                    const formattedDate = new Intl.DateTimeFormat("en-US", {
                        dateStyle: "short",
                        timeStyle: "short",
                        timeZone: "America/New_York"
                    }).format(utcDate);
                    setformattedDate(formattedDate);
                }
            } catch (error) {
                setformattedDate('Error displaying date');
            }
        }
    }, [lastUpdatedDate, lastUpdatedBy]);
    
    return (
        <Container className="practice-alerts-section sc-glass sc-shadow sc-rounded mb-4 p-3">
            <Row className="text-light">
                <Col lg="12" md="12" sm="12">
                    <h2 onClick={toggleCollapse} className="d-none d-md-flex hoverable justify-content-between">
                        Practice Alerts
                        <span className="hoverable">
                            <FontAwesomeIcon
                                icon={faAngleDown}
                                className={`hoverable rotate-animated ${
                                    isCollapsed ? `rotated` : ``
                                }`}
                            />
                            &nbsp;
                        </span>
                    </h2>
                    <h3 className="py-2 pb-3 d-flex justify-content-between border-bottom border-light d-md-none">Practice Alerts</h3>
                </Col>
                <Collapse className="w-100" isOpen={!isCollapsed}>
                    <Col lg="12" md="12" sm="12">
                        <Form>
                            <FormGroup className="mt-3">
                                <Label className="" hidden>{'Practice Alerts:'}</Label>
                                <Input
                                    type="textarea"
                                    rows="8"
                                    value={alertText}
                                    onChange={(e) => setAlertText(e.target.value)}
                                    maxLength={5000}
                                    disabled={!isEditing}
                                />
                            </FormGroup>
                        </Form>
                    </Col>
                    <Row className="px-3">
                        <Col lg="6" md="6" sm="12">
                            <small>
                                <em>
                                {lastUpdatedBy && lastUpdatedDate
                                    ? `Last updated ${formattedDate} by ${lastUpdatedBy}`
                                    : ''}
                                </em>
                            </small>
                        </Col>
                        <Col className="d-flex justify-content-end" lg="6" md="6" sm="12">
                            {isEditing ? (
                                <>
                                    <Button color="secondary" className='mr-3' onClick={handleCancelClick}>
                                        Cancel
                                    </Button>
                                    <Button color="primary" onClick={handleSaveClick}>
                                        Save
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {alertText.trim() !== '' && (
                                        <Button color="danger" className='mr-3' onClick={handleDeleteClick}>
                                            Delete
                                        </Button>
                                    )}
                                    <Button color="primary" onClick={() => setIsEditing(true)}>
                                        Edit
                                    </Button>
                                </>
                            )}
                        </Col>
                    </Row>
                </Collapse>
            </Row>
            <Modal className='practice-alerts-modal' isOpen={showConfirm} toggle={() => setShowConfirm(false)}>
                <ModalHeader toggle={() => setShowConfirm(false)}>
                    Confirm Deletion
                </ModalHeader>
                <ModalBody>
                    <p>Are you sure you want to delete this practice alert?</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" onClick={confirmDelete}>Yes</Button>
                    <Button color="secondary" onClick={() => setShowConfirm(false)}>No</Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default React.memo(PracticeAlerts);
